import { useEffect, useState } from 'react';
import { Save, TrendingUp, GraduationCap, Upload, FileText, Trash2, Loader2, Download, RefreshCw } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Field, Input, Textarea, Select } from '../components/ui';
import { toast } from '../components/toast';
import { NETWORK_LABELS, NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

const MODELS = ['gpt-4.1', 'gpt-4.1-mini', 'gpt-5', 'gpt-4o'];
const ACCEPT = '.pdf,.docx,.html,.htm,.txt,.md,.csv,.json';

export default function AiProfilePage() {
  const currentCompany = useAuthStore((s) => s.currentCompany);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openaiConfigured, setOpenaiConfigured] = useState(true);
  const [topPosts, setTopPosts] = useState([]);
  const [learning, setLearning] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState(null);
  const [fetchingPosts, setFetchingPosts] = useState(false);
  const [importing, setImporting] = useState(false);

  const companyId = currentCompany?.id;

  const loadDocs = async (profileId) => {
    if (!profileId) return;
    try {
      const r = await api.get(`/ai/profiles/${profileId}/documents`);
      setDocs(r.items || []);
    } catch { /* silencioso */ }
  };

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await api.get('/ai/profiles');
      setOpenaiConfigured(data.openaiConfigured);
      const p = (data.items && data.items[0]) || null;
      setProfile(p);
      if (p) loadDocs(p.id);
      api.get('/analytics/top-posts', { limit: 5 }).then((r) => setTopPosts(r.items || [])).catch(() => {});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !profile?.id) return;
    setUploading(true);
    try {
      for (const file of files) {
        await api.upload(`/ai/profiles/${profile.id}/documents`, file);
      }
      toast.success(`${files.length} arquivo(s) processado(s)`);
      await loadDocs(profile.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.del(`/ai/documents/${docId}`);
      await loadDocs(profile.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchRecent = async () => {
    setFetchingPosts(true);
    try {
      setRecent(await api.get('/ai/recent-posts', { days: 60 }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFetchingPosts(false);
    }
  };

  const importNetworkPosts = async () => {
    if (!profile?.id) return;
    setImporting(true);
    try {
      const res = await api.post(`/ai/profiles/${profile.id}/import-posts`, { days: 60, limit: 30 });
      toast.success(`${res.imported} posts importados como contexto`);
      await loadDocs(profile.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const learn = async () => {
    if (!profile?.id) return;
    setLearning(true);
    try {
      const res = await api.post(`/ai/profiles/${profile.id}/learn`, { limit: 5 });
      toast.success(`${res.applied} exemplos adicionados ao conhecimento`);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLearning(false);
    }
  };

  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        model: profile.model || null,
        systemPrompt: profile.systemPrompt || null,
        knowledge: profile.knowledge || null,
        imageGuidelines: profile.imageGuidelines || null,
        temperature: Number(profile.temperature) || 0.7
      };
      if (profile.id) await api.put(`/ai/profiles/${profile.id}`, payload);
      else await api.post('/ai/profiles', { ...payload, isDefault: true });
      toast.success('Perfil de IA salvo');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) return <p className="muted">Selecione uma empresa no topo.</p>;
  if (loading) return <p className="muted">Carregando…</p>;
  if (!profile) return <p className="muted">Nenhum perfil de IA. Crie uma empresa primeiro.</p>;

  return (
    <div>
      {!openaiConfigured && (
        <p className="badge yellow mb8">OpenAI nao configurada no servidor (defina OPENAI_API_KEY).</p>
      )}

      <div className="card" style={{ maxWidth: 760 }}>
        <p className="muted" style={{ marginTop: 0 }}>
          Recrie aqui o seu "GPT exclusivo" desta empresa: cole as instruções e a base de conhecimento do seu Custom GPT.
        </p>

        <div className="grid cols-2">
          <Field label="Nome do perfil"><Input value={profile.name || ''} onChange={set('name')} /></Field>
          <Field label="Modelo" hint="Vazio = modelo padrão do servidor">
            <Select value={profile.model || ''} onChange={set('model')}>
              <option value="">(padrão do servidor)</option>
              {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Instruções (system prompt)" hint="O que define o comportamento e a persona da marca">
          <Textarea rows={7} value={profile.systemPrompt || ''} onChange={set('systemPrompt')}
            placeholder="Ex: Voce e o social media da Empresa X. Tom descontraido, foco em..." />
        </Field>

        <Field label="Base de conhecimento" hint="Tom de voz, produtos, publico, exemplos de posts que funcionaram">
          <Textarea rows={7} value={profile.knowledge || ''} onChange={set('knowledge')} />
        </Field>

        <Field label="Identidade visual (para imagens)" hint="Cores da marca, estilo, mood — usado ao gerar imagens. Ex: cores roxo (#6E59F2) e branco, estilo clean e moderno, fundos claros">
          <Textarea rows={4} value={profile.imageGuidelines || ''} onChange={set('imageGuidelines')}
            placeholder="Ex: paleta roxo e branco, estilo minimalista, luz natural, sem fundos poluidos" />
        </Field>

        <Field label={`Criatividade (temperature): ${profile.temperature ?? 0.7}`}>
          <input type="range" min="0" max="1.5" step="0.1" value={profile.temperature ?? 0.7}
            onChange={set('temperature')} style={{ width: '100%' }} />
        </Field>

        <Button onClick={save} loading={saving}><Save size={16} /> Salvar perfil</Button>
      </div>

      <div className="card mt16" style={{ maxWidth: 760 }}>
        <h3 style={{ marginTop: 0 }}><FileText size={17} /> Arquivos da marca</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Suba documentos com dados da marca (manual de marca, catálogo, tom de voz, FAQ…). A gente extrai o texto e a IA
          usa como conhecimento na geração — sem precisar copiar e colar.
        </p>

        <label className="upload-drop">
          <input type="file" multiple accept={ACCEPT} onChange={handleUpload} disabled={uploading || !profile?.id} hidden />
          {uploading
            ? <><Loader2 size={18} className="spin" style={{ animation: 'spin 1s linear infinite' }} /> Processando…</>
            : <><Upload size={18} /> Clique para enviar arquivos (PDF, DOCX, HTML, TXT, MD, CSV, JSON)</>}
        </label>

        {docs.length > 0 && (
          <div className="grid mt16" style={{ gap: 8 }}>
            {docs.map((d) => (
              <div key={d.id} className="row between doc-row">
                <div className="row" style={{ gap: 9, minWidth: 0 }}>
                  <FileText size={16} className="muted" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                  <span className="badge gray">{(d.charCount || 0).toLocaleString('pt-BR')} caracteres</span>
                </div>
                <Button variant="ghost" className="sm" onClick={() => deleteDoc(d.id)}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mt16" style={{ maxWidth: 760 }}>
        <div className="row between">
          <h3 style={{ margin: 0 }}><Download size={17} /> Posts atuais das redes</h3>
          <Button variant="secondary" className="sm" onClick={fetchRecent} loading={fetchingPosts}>
            <RefreshCw size={14} /> Buscar posts
          </Button>
        </div>
        <p className="muted">
          Puxa os posts já publicados da marca no Metricool (últimos 60 dias) para a IA entender a voz real e usar como contexto.
        </p>

        {recent && recent.perNetwork && (
          <p className="hint">
            {recent.perNetwork.map((n) => `${n.network}: ${n.connected ? n.count + ' posts' : 'não conectado'}`).join(' · ')}
          </p>
        )}

        {recent && recent.count > 0 && (
          <>
            <p className="hint">{recent.count} post(s) encontrados.</p>
            <div className="grid" style={{ gap: 8, maxHeight: 280, overflow: 'auto' }}>
              {recent.posts.slice(0, 12).map((p, i) => {
                const Icon = NETWORK_ICONS[p.network] || FileText;
                return (
                  <div key={i} className="doc-row">
                    <div className="row" style={{ gap: 8 }}>
                      <Icon size={14} color={NETWORK_COLORS[p.network] || '#9aa3af'} />
                      <strong style={{ fontSize: 13 }}>{NETWORK_LABELS[p.network] || p.network || 'rede'}</strong>
                      <span className="muted" style={{ fontSize: 12 }}>{p.likes}❤ · {p.comments}💬 · {p.shares}↪</span>
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{(p.text || '').slice(0, 180)}{(p.text || '').length > 180 ? '…' : ''}</div>
                  </div>
                );
              })}
            </div>
            <Button className="mt16" onClick={importNetworkPosts} loading={importing} disabled={recent.count === 0}>
              <Download size={15} /> Importar como contexto da IA
            </Button>
          </>
        )}
        {recent && recent.count === 0 && (
          <p className="hint">Nenhum post publicado encontrado no período. As redes conectadas podem ainda não ter posts ingeridos pelo Metricool.</p>
        )}
      </div>

      <div className="card mt16" style={{ maxWidth: 760 }}>
        <h3 style={{ marginTop: 0 }}><TrendingUp size={17} /> Aprender com os melhores</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Injeta os posts de melhor desempenho como exemplos na base de conhecimento — a IA passa a imitar o que engaja.
        </p>
        {topPosts.length === 0 ? (
          <p className="muted">Sem dados de desempenho ainda (publique e aguarde o sync de métricas).</p>
        ) : (
          <div className="grid">
            {topPosts.map((t) => (
              <div key={t.postTargetId} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                <div className="row">
                  <span className="badge blue">{t.network}</span>
                  <span className="muted" style={{ fontSize: 12 }}>{t.likes} curtidas · {t.comments} coment. · {t.shares} compart.</span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{(t.text || '').slice(0, 160)}{(t.text || '').length > 160 ? '…' : ''}</div>
              </div>
            ))}
          </div>
        )}
        <Button className="mt16" variant="secondary" onClick={learn} loading={learning} disabled={topPosts.length === 0}>
          <GraduationCap size={15} /> Adicionar aos exemplos
        </Button>
      </div>
    </div>
  );
}
